import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';
import { Button } from 'react-aria-components';

import { tv } from '../../tv';
import { AiIcon } from '../ai-icon';

/**
 * A state the system computed, as a word on a pill: Active, Expired,
 * In progress.
 *
 * One of three look-alike components with different owners (see
 * docs/specs/frontend/design-system/badge-status-tag.md): a Badge is what the
 * system counts, a Status is the state it computed, a Tag is what a user
 * attached. A count rendered as a Status, or a user label rendered as one, is
 * a bug rather than a variant.
 *
 * `tone` is the closed semantic set, not a hue: a call site says what the
 * state MEANS and never which colour that is. `critical` reads the same red
 * scale the form system calls `error`; one scale, two vocabulary entries,
 * because "error" describes a validation failure and "critical" describes a
 * state. There is no teal, cyan or yellow tone: progress states are `info`
 * and warning is orange, which is where eight of the product's nine old
 * status maps had already landed.
 *
 * The state word carries the meaning; the tone and the dot only reinforce it
 * (WCAG 1.4.1, the same rule that shaped Score and Callout). That is why the
 * children are required and there is no dot-only variant.
 *
 * Removal is Tag's slot, adopted with Tag's exact contract, and it carries a
 * meaning constraint the spec spells out: rendering the remove button
 * asserts the chip is a removable SELECTION of a status, a seat in a filter
 * bar or combobox, never that the status itself is detachable. A state the
 * system computed cannot be dismissed; Status-as-state never renders the
 * slot. `removeLabel` is required with `onRemove` because the button is
 * icon-only and the name is all a screen reader gets.
 */

/* Three fill styles, every colour read from the tone cascade (tokens.md)
 * through data-tone on the root; neutral, chrome rather than a hue, keeps its
 * own three pairings in the compound table.
 *
 *   soft     the default: the tone's wash (step 3) under its text step, the
 *            dot on the solid step, the one solid element in the anatomy.
 *   solid    the tone's fill under its contrast text, the dot in that text
 *            colour. Neutral darkens to step 11 in light (gray 9 is 3.3:1
 *            under white) and keeps step 9 in dark. Warning's orange 9 takes
 *            the dark pole, the one solid white fails the 3:1 floor on.
 *   outline  transparent, the tone's border step and text step, the dot on
 *            the solid step; the one variant whose border is visible. */
const statusStyles = tv({
  slots: {
    root: [
      'ck:inline-flex ck:items-center ck:gap-1 ck:rounded-full ck:border',
      'ck:text-xs ck:font-medium ck:min-w-0 ck:max-w-full',
    ],
    dot: 'ck:size-1.5 ck:shrink-0 ck:rounded-full',
    /* min-w-0 + truncate: a long state word inside a tight table cell cuts
     * with an ellipsis instead of pushing the pill wider than its column. */
    label: 'ck:min-w-0 ck:truncate',
    /* Tag's remove anatomy verbatim: the button inherits the pill's text
     * colour and signals with opacity, so it needs no per-tone variant and
     * follows the palette for free. The focus ring is the standard one,
     * snapped rather than transitioned. */
    remove: [
      'ck:inline-flex ck:items-center ck:justify-center ck:shrink-0 ck:rounded-1',
      'ck:cursor-pointer ck:opacity-70 ck:hover:opacity-100 ck:focus-visible:focus-ring',
    ],
    removeIcon: 'ck:size-3',
  },
  variants: {
    /* Every colour reads the tone cascade (tokens.md) through data-tone on
     * the root; neutral, which is chrome rather than a hue, keeps its own
     * three pairings below. */
    variant: {
      soft: { root: 'ck:bg-tone-bg ck:border-transparent ck:text-tone-text', dot: 'ck:bg-tone' },
      solid: {
        root: 'ck:bg-fill ck:border-transparent ck:text-tone-contrast',
        dot: 'ck:bg-tone-contrast',
      },
      outline: {
        root: 'ck:bg-transparent ck:border-tone-border ck:text-tone-text',
        dot: 'ck:bg-tone',
      },
    },
    tone: { neutral: {}, info: {}, success: {}, warning: {}, critical: {}, ai: {} },
    pulsing: { true: { root: 'ck:animate-halo' }, false: {} },
    /* The heights are shared with Tag, so a computed state and an attached
     * label sit on the same line in a table cell without wobble; the corner
     * treatment (full round with a dot versus square) is what tells them
     * apart. md is 20px, the height the Figma symbols draw and the height
     * every chip in the product already rendered at (Chakra Tag defaulted to
     * its 20px sm), so the default drops into tables without moving rows;
     * sm is the 16px compact step, with tighter padding and a smaller dot
     * to keep the anatomy proportionate. */
    size: {
      sm: { root: 'ck:h-4 ck:px-pill', dot: 'ck:size-1' },
      md: { root: 'ck:h-5 ck:px-2' },
    },
  },
  compoundVariants: [
    /* The AI tone's anatomy (ai.md): the signature ring instead of a flat
     * edge and the sparkle in the dot's slot; the solid is the gradient by
     * the cascade's own rule (a tone's solid is its signature). */
    {
      tone: 'ai',
      variant: 'soft',
      class: { root: 'ck:ring-signature ck:bg-tone-bg ck:border-transparent ck:text-tone-text' },
    },
    {
      tone: 'ai',
      variant: 'outline',
      class: {
        root: 'ck:ring-signature ck:bg-transparent ck:border-transparent ck:text-tone-text',
      },
    },
    {
      tone: 'neutral',
      variant: 'soft',
      class: { root: 'ck:bg-neutral-a-3 ck:text-neutral-11', dot: 'ck:bg-neutral-9' },
    },
    /* The AA deviation; see the anatomy note above. */
    {
      tone: 'neutral',
      variant: 'solid',
      class: {
        root: 'ck:bg-none ck:bg-neutral-11 ck:dark:bg-neutral-9 ck:text-white',
        dot: 'ck:bg-white',
      },
    },
    {
      tone: 'neutral',
      variant: 'outline',
      class: { root: 'ck:text-neutral-11', dot: 'ck:bg-neutral-9' },
    },
  ],
  defaultVariants: { variant: 'soft', size: 'md' },
});

/** What a state means, the closed semantic set a Status takes as `tone`. */
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'critical' | 'ai';

interface StatusBaseProps {
  /**
   * What the state means: `neutral` for states with no judgement (Draft,
   * Archived), `info` for in-flight ones (In progress, Ready for review),
   * `success` for completed and healthy, `warning` for needs-attention-soon,
   * `critical` for failed, overdue and rejected.
   */
  tone: StatusTone;
  /**
   * The agent is working on this entity right now: a slow halo in the
   * tone's own colour (ai.md). Presence, not progress.
   */
  isPulsing?: boolean;
  /**
   * The fill style, the same axis Tag and Badge carry. `soft` (the default)
   * is the standard state look; `solid` fills with the scale's solid step
   * for the rare state that must carry weight; `outline` keeps the
   * surface's own background for dense compositions. Orthogonal to `tone`
   * and to the dot: a variant changes weight, never meaning.
   */
  variant?: 'soft' | 'solid' | 'outline';
  /** Shared with Tag, so the two sit level in the same row. */
  size?: 'sm' | 'md';
  /** The leading dot. On by default; off for very dense surfaces. */
  hasDot?: boolean;
  /** The state word. Required: the tone reinforces it, never replaces it. */
  children: React.ReactNode;
  id?: string;
  /** Test hook; end-to-end selectors address a status by name. */
  'data-testid'?: string;
}

export type StatusProps = StatusBaseProps &
  (
    | {
        /**
         * Removes the chip. Renders the trailing remove button, and with it
         * an assertion about what the chip IS: a removable selection of a
         * status (a filter or combobox seat), never a state the system
         * computed. Status-as-state never passes this.
         */
        onRemove: () => void;
        /**
         * Names the icon-only remove button for assistive technology
         * ("Remove status Approved"). Required with `onRemove`: the name is
         * all a screen reader gets.
         */
        removeLabel: string;
      }
    | { onRemove?: undefined; removeLabel?: undefined }
  );

export const Status = React.forwardRef<HTMLSpanElement, StatusProps>(function Status(
  {
    tone,
    variant = 'soft',
    size = 'md',
    hasDot = true,
    isPulsing = false,
    children,
    onRemove,
    removeLabel,
    ...props
  },
  ref,
) {
  const { root, dot, label, remove, removeIcon } = statusStyles({
    tone,
    variant,
    size,
    pulsing: isPulsing,
  });
  return (
    <span {...props} ref={ref} className={root()} data-component="status" data-tone={tone}>
      {/* Decoration: the state word beside it says everything the dot does. */}
      {hasDot && tone === 'ai' ? (
        <AiIcon size="sm" fill={variant === 'solid' ? 'current' : 'gradient'} />
      ) : hasDot ? (
        <span className={dot()} aria-hidden="true" />
      ) : null}
      <span className={label()}>{children}</span>
      {onRemove ? (
        /* Keep removal out of parent select/drag interactions, and keep the
         * combobox input focused so this button stays mounted through press. */
        <Button
          onPress={onRemove}
          onPointerDown={(event) => event.stopPropagation()}
          aria-label={removeLabel}
          className={remove()}
          preventFocusOnPress
        >
          <XMarkIcon className={removeIcon()} aria-hidden />
        </Button>
      ) : null}
    </span>
  );
});
